import { SynthParams } from '../core/project/types';

export interface SynthPreset {
  name: string;
  category: string;
  params: Partial<SynthParams>;
}

/**
 * Built-in synth preset library. Params are partial — applied on top of the
 * channel's current synth settings (FL-style "load into channel").
 */
export const SYNTH_PRESETS: SynthPreset[] = [
  // ── Bass ──────────────────────────────────────────────
  {
    name: 'Deep Sub',
    category: 'Bass',
    params: {
      osc1Wave: 'sine',
      osc1Octave: -1,
      osc2Wave: 'sine',
      osc2Octave: -2,
      osc2Level: 0.4,
      fmDepth: 0,
      filterType: 'lowpass',
      filterCutoff: 300,
      filterResonance: 2,
      filterEnvAmount: 0.2,
      lfoDepth: 0,
      attack: 0.004,
      decay: 0.15,
      sustain: 0.8,
      release: 0.1,
    },
  },
  {
    name: 'Acid Squelch',
    category: 'Bass',
    params: {
      osc1Wave: 'sawtooth',
      osc1Octave: -1,
      osc2Wave: 'square',
      osc2Octave: -1,
      osc2Level: 0.3,
      fmDepth: 0.2,
      filterType: 'lowpass',
      filterCutoff: 400,
      filterResonance: 14,
      filterEnvAmount: 0.9,
      lfoRate: 6,
      lfoDepth: 0.3,
      lfoTarget: 'filter',
      attack: 0.002,
      decay: 0.2,
      sustain: 0.3,
      release: 0.08,
    },
  },
  {
    name: 'Pluck Bass',
    category: 'Bass',
    params: {
      osc1Wave: 'sawtooth',
      osc1Octave: -1,
      osc2Wave: 'sawtooth',
      osc2Octave: -1,
      osc2Detune: 8,
      osc2Level: 0.5,
      fmDepth: 0,
      filterType: 'lowpass',
      filterCutoff: 1200,
      filterResonance: 4,
      filterEnvAmount: 0.8,
      attack: 0.001,
      decay: 0.18,
      sustain: 0.05,
      release: 0.12,
    },
  },
  // ── Lead ──────────────────────────────────────────────
  {
    name: 'Square Lead',
    category: 'Lead',
    params: {
      osc1Wave: 'square',
      osc1Octave: 0,
      osc2Wave: 'square',
      osc2Octave: 0,
      osc2Detune: 7,
      osc2Level: 0.6,
      fmDepth: 0,
      filterType: 'lowpass',
      filterCutoff: 3500,
      filterResonance: 3,
      filterEnvAmount: 0.3,
      attack: 0.005,
      decay: 0.1,
      sustain: 0.85,
      release: 0.15,
    },
  },
  {
    name: 'Saw Lead',
    category: 'Lead',
    params: {
      osc1Wave: 'sawtooth',
      osc1Octave: 0,
      osc2Wave: 'sawtooth',
      osc2Octave: 0,
      osc2Detune: 10,
      osc2Level: 0.7,
      fmDepth: 0,
      filterType: 'lowpass',
      filterCutoff: 5000,
      filterResonance: 5,
      filterEnvAmount: 0.4,
      lfoRate: 5,
      lfoDepth: 0.2,
      lfoTarget: 'filter',
      attack: 0.01,
      decay: 0.1,
      sustain: 0.9,
      release: 0.2,
    },
  },
  {
    name: 'Vapor Pluck',
    category: 'Lead',
    params: {
      osc1Wave: 'triangle',
      osc1Octave: 0,
      osc2Wave: 'sine',
      osc2Octave: 1,
      osc2Level: 0.5,
      fmDepth: 0.15,
      filterType: 'lowpass',
      filterCutoff: 4000,
      filterResonance: 2,
      filterEnvAmount: 0.6,
      attack: 0.002,
      decay: 0.25,
      sustain: 0.1,
      release: 0.3,
    },
  },
  // ── Pad ───────────────────────────────────────────────
  {
    name: 'Warm Pad',
    category: 'Pad',
    params: {
      osc1Wave: 'sawtooth',
      osc1Octave: 0,
      osc1Detune: -6,
      osc2Wave: 'sawtooth',
      osc2Octave: 0,
      osc2Detune: 6,
      osc2Level: 0.8,
      fmDepth: 0,
      filterType: 'lowpass',
      filterCutoff: 1800,
      filterResonance: 1,
      filterEnvAmount: 0.15,
      lfoRate: 0.3,
      lfoDepth: 0.4,
      lfoTarget: 'filter',
      attack: 0.6,
      decay: 0.3,
      sustain: 0.8,
      release: 1.2,
    },
  },
  {
    name: 'Dream Pad',
    category: 'Pad',
    params: {
      osc1Wave: 'triangle',
      osc1Octave: 0,
      osc2Wave: 'sine',
      osc2Octave: 1,
      osc2Level: 0.7,
      fmDepth: 0.1,
      filterType: 'lowpass',
      filterCutoff: 2500,
      filterResonance: 1,
      filterEnvAmount: 0.1,
      lfoRate: 0.2,
      lfoDepth: 0.5,
      lfoTarget: 'filter',
      attack: 0.8,
      decay: 0.4,
      sustain: 0.9,
      release: 1.5,
    },
  },
  // ── FM ────────────────────────────────────────────────
  {
    name: 'FM Bell',
    category: 'FM',
    params: {
      osc1Wave: 'sine',
      osc1Octave: 0,
      osc2Wave: 'sine',
      osc2Octave: 2,
      osc2Level: 0.9,
      fmDepth: 0.9,
      filterType: 'lowpass',
      filterCutoff: 8000,
      filterResonance: 1,
      filterEnvAmount: 0.2,
      attack: 0.001,
      decay: 0.6,
      sustain: 0.05,
      release: 0.8,
    },
  },
  {
    name: 'FM Brass',
    category: 'FM',
    params: {
      osc1Wave: 'sawtooth',
      osc1Octave: 0,
      osc2Wave: 'sawtooth',
      osc2Octave: 0,
      osc2Level: 0.8,
      fmDepth: 0.5,
      filterType: 'lowpass',
      filterCutoff: 3000,
      filterResonance: 3,
      filterEnvAmount: 0.3,
      attack: 0.02,
      decay: 0.2,
      sustain: 0.7,
      release: 0.3,
    },
  },
  // ── Keys ──────────────────────────────────────────────
  {
    name: 'Glass Keys',
    category: 'Keys',
    params: {
      osc1Wave: 'triangle',
      osc1Octave: 0,
      osc2Wave: 'sine',
      osc2Octave: 2,
      osc2Level: 0.4,
      fmDepth: 0.3,
      filterType: 'lowpass',
      filterCutoff: 6000,
      filterResonance: 1.5,
      filterEnvAmount: 0.3,
      attack: 0.001,
      decay: 0.4,
      sustain: 0.15,
      release: 0.5,
    },
  },
  {
    name: 'EP Rhodes',
    category: 'Keys',
    params: {
      osc1Wave: 'sine',
      osc1Octave: 0,
      osc2Wave: 'triangle',
      osc2Octave: 0,
      osc2Level: 0.5,
      fmDepth: 0.25,
      filterType: 'lowpass',
      filterCutoff: 2200,
      filterResonance: 1,
      filterEnvAmount: 0.15,
      attack: 0.005,
      decay: 0.3,
      sustain: 0.4,
      release: 0.4,
    },
  },
];